import { baseApi } from '@/Redux/baseApi';
import type {
  IntegrationCategory,
  IntegrationCredentialKind,
  IntegrationDeliveryDirection,
  IntegrationDeliveryStatus,
  IntegrationHealth,
  IntegrationInstallationStatus,
  IntegrationJobKind,
  IntegrationJobStatus,
} from '@/lib/shared-types';

/**
 * # The whole §2 integration-substrate contract, pinned in one file
 *
 * Read directly from `src/modules/integrations/` on `integrate/wave-a`
 * (merged, green — see `docs/ADR/ADR-0002-integration-platform.md`):
 *   - `integrations.controller.ts` for every route below
 *   - `dto/index.ts` for the request bodies
 *   - `integrations.service.ts`, `integration-credentials.service.ts`,
 *     `integration-delivery.service.ts`, `integration-sync.service.ts` and
 *     `integration-oauth.service.ts` for the response shapes — none of it is
 *     decorated with `@ApiResponse`, so every interface below is read off
 *     what the service actually returns, not off Swagger.
 *
 * The backend is finished and merged, but the discipline stays: a rename on
 * that side is a one-file fix here instead of a hunt through six screens.
 *
 * ## The pagination envelope is `{ items, total, page, limit, pages }`
 *
 * Every list route runs its query through `common/dto/pagination.dto.ts`'s
 * `paginate()` verbatim — not this dashboard's more common `{ data, meta }`
 * shape. `IntegrationPage<T>` names it once.
 *
 * ## Reads only
 *
 * Mutations (install, configure, uninstall, put-credential, start/cancel job,
 * replay, replay-dead-letters, begin-oauth) go through the `api` axios client
 * directly, from the component that performs it. A replay or an uninstall
 * needs the refusal's own sentence — `BadRequestException` bodies here are
 * plain English, not a `{ code }` — and RTK Query's mutation error shape is
 * worse at surfacing that than reading `error.response.data.message` at the
 * call site. Every write below invalidates the `Integration` tag by hand.
 */

export const INTEGRATIONS_ROUTES = {
  providers: '/integrations/providers',
  installations: '/integrations/installations',
  installation: (id: string) => `/integrations/installations/${id}`,
  installationHealth: (id: string) => `/integrations/installations/${id}/health`,
  installationCredentials: (id: string) => `/integrations/installations/${id}/credentials`,
  installationOAuthAuthorize: (id: string) => `/integrations/installations/${id}/oauth/authorize`,
  installationCursors: (id: string) => `/integrations/installations/${id}/cursors`,
  installationJobs: (id: string) => `/integrations/installations/${id}/jobs`,
  installationReplayDeadLetters: (id: string) =>
    `/integrations/installations/${id}/replay-dead-letters`,
  deliveries: '/integrations/deliveries',
  delivery: (id: string) => `/integrations/deliveries/${id}`,
  deliveryReplay: (id: string) => `/integrations/deliveries/${id}/replay`,
  jobs: '/integrations/jobs',
  job: (id: string) => `/integrations/jobs/${id}`,
} as const;

/**
 * The seven keys `src/common/constants/permissions.ts` registers under
 * `integrations`. Deliberately seven and not the usual view/create/update/
 * delete: `sync` and `replay` MOVE data (a replay resends a real order to a
 * real provider) so they are separable from `configure`, which only edits
 * settings, and from `credentials.rotate`, which is separable from both
 * because it is the one route that replaces the secret that authorises the
 * whole connection. There is no `integrations.credentials.view` — a
 * credential is never returned after creation, so there is nothing such a key
 * would admit anyone to.
 */
export const INTEGRATIONS_PERMISSIONS = {
  view: 'integrations.view',
  install: 'integrations.install',
  configure: 'integrations.configure',
  credentialsRotate: 'integrations.credentials.rotate',
  sync: 'integrations.sync',
  replay: 'integrations.replay',
  uninstall: 'integrations.uninstall',
} as const;

export interface IntegrationPage<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

const emptyPage = <T,>(page?: number, limit?: number): IntegrationPage<T> => ({
  items: [],
  total: 0,
  page: page ?? 1,
  limit: limit ?? 25,
  pages: 1,
});

// ------------------------------------------------------------ the catalogue

/** `GET /integrations/providers` row — `IntegrationsService.listProviders`. */
export interface IntegrationProvider {
  id: string;
  key: string;
  name: string;
  category: IntegrationCategory;
  authKind: 'API_KEY' | 'OAUTH2' | 'NONE';
  contractVersion: number;
  scopes: string[];
  events: string[];
  syncResources: string[];
  featureFlagKey: string | null;
  description: string | null;
  docsUrl: string | null;
  isActive: boolean;
  /** From the connector's own descriptor, joined in by the service — not a column. */
  capabilities: Array<'inbound' | 'oauth' | 'outbound' | 'sync' | 'reconcile'>;
  requiredCredentials: IntegrationCredentialKind[];
}

export interface ListProvidersQuery {
  page?: number;
  limit?: number;
  category?: IntegrationCategory;
  search?: string;
  includeInactive?: boolean;
}

// --------------------------------------------------------------- an install

export interface InstallationBranchRef {
  id: string;
  name: string;
  code: string;
}

export interface InstallationProviderRef {
  id: string;
  key: string;
  name: string;
  category: IntegrationCategory;
}

/** `GET /integrations/installations` row. */
export interface IntegrationInstallation {
  id: string;
  tenantId: string;
  branchId: string | null;
  providerId: string;
  providerKey: string;
  contractVersion: number;
  status: IntegrationInstallationStatus;
  health: IntegrationHealth;
  config: Record<string, unknown>;
  grantedScopes: string[];
  subscribedEvents: string[];
  endpointUrl: string | null;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  installedById: string | null;
  uninstalledAt: string | null;
  uninstalledById: string | null;
  createdAt: string;
  updatedAt: string;
  provider?: InstallationProviderRef;
  branch?: InstallationBranchRef | null;
}

/** The one field `POST /integrations/installations` adds to the row. */
export interface InstallResult extends IntegrationInstallation {
  missingCredentials: IntegrationCredentialKind[];
}

/** `GET /integrations/installations/:id` — the row plus its credentials. `provider` is the full row (`include: { provider: true }`), not the narrow list-row select. */
export interface IntegrationInstallationDetail extends IntegrationInstallation {
  provider: InstallationProviderRef & {
    authKind: 'API_KEY' | 'OAUTH2' | 'NONE';
    scopes: string[];
    events: string[];
  };
  credentials: PublicCredential[];
  missingCredentials: IntegrationCredentialKind[];
}

export interface ListInstallationsQuery {
  page?: number;
  limit?: number;
  providerKey?: string;
  branchId?: string;
  status?: IntegrationInstallationStatus;
  includeUninstalled?: boolean;
}

export interface InstallIntegrationBody {
  providerKey: string;
  branchId?: string;
  scopes?: string[];
  subscribedEvents?: string[];
  endpointUrl?: string;
  config?: Record<string, unknown>;
}

export interface ConfigureInstallationBody {
  config?: Record<string, unknown>;
  scopes?: string[];
  subscribedEvents?: string[];
  endpointUrl?: string;
  status?: 'ACTIVE' | 'SUSPENDED';
}

// -------------------------------------------------------------- credentials

/**
 * `CREDENTIAL_PUBLIC_SELECT` on the wire. Never a `secretCiphertext` field —
 * there is no route that returns one, at creation or ever after. A screen may
 * show `publicRef` (the non-secret half: a client id, a key prefix) to say
 * WHICH secret is installed, never the secret itself.
 */
export interface PublicCredential {
  id: string;
  installationId: string;
  kind: IntegrationCredentialKind;
  keyId: string;
  publicRef: string | null;
  version: number;
  expiresAt: string | null;
  rotatedAt: string | null;
  rotatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PutCredentialBody {
  kind: IntegrationCredentialKind;
  secret: string;
  publicRef?: string;
  expiresAt?: string;
}

// ------------------------------------------------------------------- health

/** `GET /integrations/installations/:id/health` — `IntegrationsService.health`. */
export interface InstallationHealth {
  installationId: string;
  providerKey: string;
  status: IntegrationInstallationStatus;
  health: IntegrationHealth;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastError: string | null;
  /** The rollup window, in hours. Always 24 today. */
  windowHours: number;
  /** Status -> count, over the window. Only statuses seen in the window are keys. */
  deliveries: Partial<Record<IntegrationDeliveryStatus, number>>;
  /** The error history the blueprint asks for — each names the event that failed, not a count. */
  recentFailures: Array<{
    id: string;
    direction: IntegrationDeliveryDirection;
    eventName: string;
    status: IntegrationDeliveryStatus;
    attempts: number;
    responseStatus: number | null;
    lastError: string | null;
    failedAt: string | null;
    deadLetteredAt: string | null;
    createdAt: string;
  }>;
}

// ---------------------------------------------------------------- delivery

/** `GET /integrations/deliveries` row and `GET /integrations/deliveries/:id`. */
export interface IntegrationDelivery {
  id: string;
  tenantId: string;
  installationId: string;
  direction: IntegrationDeliveryDirection;
  eventName: string;
  /** What a duplicate insert is caught on — the dedupe key a replay derives from. */
  idempotencyKey: string;
  payload: unknown;
  status: IntegrationDeliveryStatus;
  attempts: number;
  responseStatus: number | null;
  lastError: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
  deadLetteredAt: string | null;
  /** Set on a row an operator's replay created; points at the delivery that actually happened. */
  replayOfId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListDeliveriesQuery {
  page?: number;
  limit?: number;
  installationId?: string;
  direction?: IntegrationDeliveryDirection;
  status?: IntegrationDeliveryStatus;
  eventName?: string;
  /** The triage view. Overrides `status` on the server, because that is what asking for it means. */
  deadLetteredOnly?: boolean;
}

// --------------------------------------------------------------- sync / jobs

/** `GET /integrations/installations/:id/cursors` row. */
export interface IntegrationSyncCursor {
  id: string;
  installationId: string;
  resource: string;
  /** Null with a null `lastSyncedAt`: never started. Null WITH one: the provider says there is no more. */
  cursor: string | null;
  lastSyncedAt: string | null;
  lastJobId: string | null;
  recordsSynced: number;
}

/** `GET /integrations/jobs` row and `GET /integrations/jobs/:id`. */
export interface IntegrationJob {
  id: string;
  tenantId: string;
  installationId: string;
  kind: IntegrationJobKind;
  status: IntegrationJobStatus;
  resource: string | null;
  cursorBefore: string | null;
  cursorAfter: string | null;
  recordsSeen: number;
  recordsApplied: number;
  discrepancies: number | null;
  attempts: number;
  lastError: string | null;
  requestedById: string | null;
  scheduledFor: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

export interface ListJobsQuery {
  page?: number;
  limit?: number;
  installationId?: string;
  kind?: IntegrationJobKind;
  status?: IntegrationJobStatus;
}

export interface StartSyncJobBody {
  kind: IntegrationJobKind;
  resource?: string;
  scheduledFor?: string;
}

// ------------------------------------------------------------------- oauth

export interface BeginOAuthResult {
  authorizeUrl: string;
}

// ------------------------------------------------------------------ endpoints

export const integrationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /** `GET /integrations/providers` — `integrations.view`. */
    integrationProviders: build.query<IntegrationPage<IntegrationProvider>, ListProvidersQuery | void>({
      query: (params) => ({ url: INTEGRATIONS_ROUTES.providers, params: params ?? undefined }),
      transformResponse: (response: unknown): IntegrationPage<IntegrationProvider> => {
        const p = response as Partial<IntegrationPage<IntegrationProvider>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: [{ type: 'Integration' as const, id: 'PROVIDERS' }],
    }),

    /** `GET /integrations/installations` — `integrations.view`. */
    integrationInstallations: build.query<
      IntegrationPage<IntegrationInstallation>,
      ListInstallationsQuery | void
    >({
      query: (params) => ({ url: INTEGRATIONS_ROUTES.installations, params: params ?? undefined }),
      transformResponse: (response: unknown): IntegrationPage<IntegrationInstallation> => {
        const p = response as Partial<IntegrationPage<IntegrationInstallation>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: (result) => [
        { type: 'Integration' as const, id: 'INSTALLATIONS' },
        ...(result?.items ?? []).map((i) => ({ type: 'Integration' as const, id: i.id })),
      ],
    }),

    /** `GET /integrations/installations/:id` — `integrations.view`. */
    integrationInstallation: build.query<IntegrationInstallationDetail | null, string>({
      query: (id) => ({ url: INTEGRATIONS_ROUTES.installation(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object'
          ? (response as IntegrationInstallationDetail)
          : null,
      providesTags: (_r, _e, id) => [{ type: 'Integration' as const, id }],
    }),

    /** `GET /integrations/installations/:id/health` — `integrations.view`. */
    integrationInstallationHealth: build.query<InstallationHealth | null, string>({
      query: (id) => ({ url: INTEGRATIONS_ROUTES.installationHealth(id) }),
      transformResponse: (response: unknown) =>
        response && typeof response === 'object' ? (response as InstallationHealth) : null,
      providesTags: (_r, _e, id) => [{ type: 'Integration' as const, id: `health:${id}` }],
    }),

    /** `GET /integrations/installations/:id/cursors` — `integrations.view`. */
    integrationCursors: build.query<IntegrationSyncCursor[], string>({
      query: (id) => ({ url: INTEGRATIONS_ROUTES.installationCursors(id) }),
      transformResponse: (response: unknown) => (Array.isArray(response) ? response : []),
      providesTags: (_r, _e, id) => [{ type: 'Integration' as const, id: `cursors:${id}` }],
    }),

    /** `GET /integrations/deliveries` — `integrations.view`. */
    integrationDeliveries: build.query<IntegrationPage<IntegrationDelivery>, ListDeliveriesQuery | void>({
      query: (params) => ({ url: INTEGRATIONS_ROUTES.deliveries, params: params ?? undefined }),
      transformResponse: (response: unknown): IntegrationPage<IntegrationDelivery> => {
        const p = response as Partial<IntegrationPage<IntegrationDelivery>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      providesTags: [{ type: 'Integration' as const, id: 'DELIVERIES' }],
    }),

    /** `GET /integrations/jobs` — `integrations.view`. */
    integrationJobs: build.query<IntegrationPage<IntegrationJob>, ListJobsQuery | void>({
      query: (params) => ({ url: INTEGRATIONS_ROUTES.jobs, params: params ?? undefined }),
      transformResponse: (response: unknown): IntegrationPage<IntegrationJob> => {
        const p = response as Partial<IntegrationPage<IntegrationJob>> | null;
        return Array.isArray(p?.items)
          ? {
              items: p!.items,
              total: p?.total ?? p!.items.length,
              page: p?.page ?? 1,
              limit: p?.limit ?? 25,
              pages: p?.pages ?? 1,
            }
          : emptyPage(1, 25);
      },
      // One `JOBS` tag rather than one per installation: this screen only ever
      // reads jobs scoped to the installation whose detail is open, so a
      // single list tag is invalidated no more often than a per-id one would
      // be and needs no id derived from the query argument.
      providesTags: [{ type: 'Integration' as const, id: 'JOBS' }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useIntegrationProvidersQuery,
  useIntegrationInstallationsQuery,
  useIntegrationInstallationQuery,
  useIntegrationInstallationHealthQuery,
  useIntegrationCursorsQuery,
  useIntegrationDeliveriesQuery,
  useIntegrationJobsQuery,
} = integrationsApi;

/** An installation changed — its status, its credentials, its config. */
export function invalidateIntegrationInstallation(id?: string) {
  return integrationsApi.util.invalidateTags([
    { type: 'Integration' as const, id: 'INSTALLATIONS' },
    ...(id
      ? [
          { type: 'Integration' as const, id },
          { type: 'Integration' as const, id: `health:${id}` },
          { type: 'Integration' as const, id: `cursors:${id}` },
        ]
      : []),
  ]);
}

/** A delivery was replayed, or a batch of dead letters was. */
export function invalidateIntegrationDeliveries(installationId?: string) {
  return integrationsApi.util.invalidateTags([
    { type: 'Integration' as const, id: 'DELIVERIES' },
    ...(installationId
      ? [
          { type: 'Integration' as const, id: installationId },
          { type: 'Integration' as const, id: `health:${installationId}` },
        ]
      : []),
  ]);
}

/** A job was started or cancelled. */
export function invalidateIntegrationJobs(installationId?: string) {
  return integrationsApi.util.invalidateTags([
    { type: 'Integration' as const, id: 'JOBS' },
    ...(installationId
      ? [
          { type: 'Integration' as const, id: `cursors:${installationId}` },
          { type: 'Integration' as const, id: `health:${installationId}` },
        ]
      : []),
  ]);
}
